//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { AddItem, RatingsReview } from '@cmp/common';
//------------------------------------------------------------------------------
// Assets ----------------------------------------------------------------------
import { logoPlaceholderUrl } from '@cmp/images';
//------------------------------------------------------------------------------
// Helpers & Constants ---------------------------------------------------------
import { shopifyFormatter } from '@helpers/formatter';
import { withReferral } from '@helpers/referrals';
import { StoreConfigContext } from '@helpers/storeConfigContext';
import { handleGtmEvent } from '@helpers/gtm';
//------------------------------------------------------------------------------
// React Class -----------------------------------------------------------------
/**
 * Refers to a product thumbnail in PDP (Products Display Page)
 */
class Product extends React.Component {
  constructor(props) {
    super(props);
    const { t } = props;
    this.state = {
      errorMessage: t('GenericErrorMessage'),
    };
    this.childErrorCb = this.childErrorCb.bind(this);
    this.childResetErrorCb = this.childResetErrorCb.bind(this);
  }

  pricingComponent(formattedProduct, isAvailable) {
    const {
      storeConfig: { currencySymbol, language },
    } = this.context;

    if (!formattedProduct) return '';

    const hasDiscount = formattedProduct.hasDiscount();
    const fullPrice = formattedProduct.fullPrice(currencySymbol, language);
    const discountedPrice = formattedProduct.discountedPrice(
      currencySymbol,
      language
    );

    if (!hasDiscount) {
      return <span className={styles.Amount}>{fullPrice}</span>;
    }

    return (
      <>
        <span
          className={classNames(styles.Amount, {
            [styles.Unavailable]: !isAvailable,
          })}
        >
          {discountedPrice}
        </span>
        <span
          className={classNames(styles.OriginalAmount, {
            [styles.Unavailable]: !isAvailable,
          })}
        >
          {fullPrice}
        </span>
      </>
    );
  }

  isAdded(product) {
    const { __referral } = this.props;
    return (
      __referral &&
      __referral.items &&
      __referral.items.findIndex(
        (x) => x.shopifyProductId.toString() === product.id.toString()
      ) > -1
    );
  }

  childErrorCb(errorMessage) {
    this.setState({
      errorMessage,
    });
  }

  childResetErrorCb() {
    const { t } = this.props;

    this.setState({
      errorMessage: t('GenericErrorMessage'),
    });
  }

  render() {
    const { t, i18n, item, metadata, review = {} } = this.props;
    const { index, listName, listId } = metadata;
    const { errorMessage = t('GenericErrorMessage') } = this.state;

    const formattedProduct = shopifyFormatter.product(item);

    const defaultVariant = shopifyFormatter.product(item).defaultVariant();
    const isAvailable = shopifyFormatter.variant(defaultVariant).isAvailable();

    const errorMessageComponentClasses = classNames(styles.ErrorMessage, {
      [styles.Visible]: errorMessage !== t('GenericErrorMessage'),
      [styles.notVisible]: errorMessage === t('GenericErrorMessage'),
    });
    let errorTokens = errorMessage.split('.');
    let errorFirst = errorTokens[0];
    let errorSecond = errorTokens.length > 1 ? errorTokens[1] : '';

    const indexedItem = { ...item, index };
    const eventParameters = {
      item_list_name: listName,
      item_list_id: listId,
    };

    // no more than two badges
    const ProductCardBadges = (props) => {
      const { onlyBrandTags } = props;
      const productTagMap = shopifyFormatter.product(item).tags();

      const productCardTags = t('ProductCardTags', {
        returnObjects: true,
      });

      const filteredTags = onlyBrandTags
        ? productCardTags.filter((tag) => tag.toLowerCase().includes('brand'))
        : productCardTags;

      const tagsForBadges = filteredTags.map((decor) => {
        return {
          key: decor,
          value: t(decor),
          style: decor,
        };
      });

      const Badges = tagsForBadges.map((tag) => {
        const isTagValueNew = tag.value.toLowerCase().includes('new');
        const isTagValueLimitedStock = tag.value
          .toLowerCase()
          .includes('limited');

        if (productTagMap?.[tag.key]) {
          return (
            <div key={tag.key}>
              <span
                className={classNames(
                  {
                    [styles.NewProductLabel]: isTagValueNew,
                  },
                  {
                    [styles.LimitedStock]: isTagValueLimitedStock,
                  },
                  styles.tagDecoratorLabel
                )}
              >
                {tag.value}
              </span>
            </div>
          );
        }
      });

      return Badges;
    };

    return (
      <li className={styles.productCardContainer}>
        <div className={styles.left}>
          <div className={classNames(styles.itemContent, styles.desktop)}>
            <div className={styles.badgesContainer}>
              <ProductCardBadges />
            </div>
          </div>
          <a
            className={classNames(styles.itemContent, styles.imageContent)}
            href={`/product/${item.id}`}
            onClick={(event) =>
              handleGtmEvent('select_item', {
                products: [indexedItem],
                context: this.context,
                eventParameters,
              })
            }
          >
            <img
              alt={item.title}
              className={classNames(styles.productImage, {
                [styles.unavailableImage]: !isAvailable,
              })}
              src={(item.image && item.image.src) || logoPlaceholderUrl(i18n)}
            />
            {!isAvailable && (
              <div className={styles.UnavailableBadgeContainer}>
                <p className={styles.UnavailableBadge}>
                  {t('dashboard_Products_List_Temporarily')}
                </p>
                <p className={styles.UnavailableBadge}>
                  {t('dashboard_Products_List_Unavailable')}
                </p>
              </div>
            )}
          </a>
        </div>
        <div className={styles.right}>
          <div className={styles.itemContent}>
            <div className={styles.RatingsContainer}>
              {t('locale') === 'en' && (
                <RatingsReview
                  variant={'plp'}
                  review={review}
                  isAvailable={isAvailable}
                />
              )}
            </div>
          </div>
          <a
            className={classNames(
              styles.itemContent,
              styles.productTitleContainer
            )}
            href={`/product/${item.id}`}
            onClick={(event) =>
              handleGtmEvent('select_item', {
                products: [indexedItem],
                context: this.context,
                eventParameters,
              })
            }
          >
            <span className={styles.ProductTitle}>
              {item.title.split('<br>').map((str, index) => (
                <span key={str}>{str}</span>
              ))}
            </span>
          </a>
          <div className={classNames(styles.itemContent, styles.mobile)}>
            <ProductCardBadges onlyBrandTags={true} />
          </div>

          <div className={styles.itemContent}>
            <div className={errorMessageComponentClasses}>
              <p>{errorFirst}</p>
              <p>{errorSecond}</p>
            </div>
          </div>
          <div
            className={classNames(
              styles.itemContent,
              styles.itemBottomPricingCta
            )}
          >
            <div
              className={classNames(styles.Pricing, {
                [styles.Added]: this.isAdded(item),
              })}
            >
              {this.pricingComponent(formattedProduct, isAvailable)}
            </div>
            <div className={styles.cta}>
              <AddItem
                index={index}
                product={item}
                errorCb={this.childErrorCb}
                errorResetCb={this.childResetErrorCb}
              />
            </div>
          </div>
        </div>
      </li>
    );
  }
}

//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withReferral(withTranslation()(Product));

Product.contextType = StoreConfigContext;
